import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  getPlugin,
  savePluginSettings,
  togglePlugin,
} from '../../store/AsyncMethods/PluginsMethod';
import { RESET_PLUGINS_MSG } from '../../store/Types/PluginsTypes';
import axios from 'axios';

const Field = ({ label, id, type = 'text', value, onChange, placeholder, helpText, required }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      id={id}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
    />
    {helpText && <p className="mt-1 text-xs text-gray-400">{helpText}</p>}
  </div>
);

const Section = ({ title, children }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5">
    <h2 className="text-base font-semibold text-gray-800 border-b border-gray-100 pb-3">{title}</h2>
    {children}
  </div>
);

function StarRating({ rating }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className={`w-4 h-4 ${star <= rating ? 'text-yellow-400' : 'text-gray-200'} fill-current`}
          viewBox="0 0 24 24"
        >
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ))}
    </div>
  );
}

export default function GoogleReviewsPlugin() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentPlugin, loading, success, error } = useSelector((s) => s.PluginsReducer);

  const [apiKey, setApiKey] = useState('');
  const [placeId, setPlaceId] = useState('');
  const [maxReviews, setMaxReviews] = useState(6);
  const [minRating, setMinRating] = useState(4);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  useEffect(() => {
    dispatch(getPlugin('google_reviews'));
  }, []);

  useEffect(() => {
    if (currentPlugin?.plugin_key === 'google_reviews') {
      setApiKey(currentPlugin.settings?.api_key || '');
      setPlaceId(currentPlugin.settings?.place_id || '');
      setMaxReviews(currentPlugin.settings?.max_reviews || 6);
      setMinRating(currentPlugin.settings?.min_rating || 4);
    }
  }, [currentPlugin]);

  useEffect(() => {
    if (success) { toast.success(success); dispatch({ type: RESET_PLUGINS_MSG }); setSaving(false); setToggling(false); }
    if (error) { toast.error(error); dispatch({ type: RESET_PLUGINS_MSG }); setSaving(false); setToggling(false); }
  }, [success, error]);

  const handleSave = () => {
    if (!apiKey.trim()) { toast.error('Google API Key is required'); return; }
    if (!placeId.trim()) { toast.error('Place ID is required'); return; }
    setSaving(true);
    dispatch(savePluginSettings('google_reviews', {
      api_key: apiKey.trim(),
      place_id: placeId.trim(),
      max_reviews: Number(maxReviews),
      min_rating: Number(minRating),
    }));
  };

  const handleToggle = () => {
    setToggling(true);
    dispatch(togglePlugin('google_reviews'));
  };

  const handlePreview = async () => {
    if (!currentPlugin?.settings?.api_key) {
      toast.error('Pehle API Key aur Place ID save karo');
      return;
    }
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:7001');
      const resp = await axios.get(`${baseUrl}/api/plugins/google-reviews/reviews`);
      setPreviewData(resp.data);
    } catch (e) {
      toast.error('Preview fetch failed. Settings save karke dobara try karo.');
    }
    setPreviewLoading(false);
  };

  const isActive = currentPlugin?.is_active;

  return (
    <div className="mx-3 pb-10 max-w-3xl">
      {/* Back + Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate('/plugins')}
          className="p-2 rounded-xl hover:bg-gray-100 transition-colors text-gray-500"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center shadow">
            <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Google Reviews</h1>
            <p className="text-sm text-gray-400">Homepage pe real Google reviews dikhao</p>
          </div>
        </div>
        {/* Active toggle */}
        <div className="flex items-center gap-2.5">
          <span className={`text-sm font-medium ${isActive ? 'text-green-600' : 'text-gray-400'}`}>
            {isActive ? 'Active' : 'Inactive'}
          </span>
          <button
            onClick={handleToggle}
            disabled={toggling || loading}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none disabled:opacity-50 ${
              isActive ? 'bg-green-500' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                isActive ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      <div className="space-y-5">
        {/* Info Banner */}
        {isActive && (
          <div className="p-4 rounded-xl bg-green-50 border border-green-100 text-sm text-green-700 flex items-start gap-2">
            <svg className="w-5 h-5 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>Plugin active hai — homepage pe "What Our Customers Say" section ki jagah Google Reviews dikh rahe hain.</span>
          </div>
        )}

        {/* Credentials */}
        <Section title="API Configuration">
          <Field
            label="Google Places API Key"
            id="api_key"
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="AIzaSy..."
            helpText="Google Cloud Console → APIs & Services → Credentials. Places API enable hona chahiye."
            required
          />
          <Field
            label="Place ID"
            id="place_id"
            value={placeId}
            onChange={(e) => setPlaceId(e.target.value)}
            placeholder="ChIJN1t_tDeuEmsRUsoyG83frY4"
            helpText="Google Maps pe apna business search karo → Share → place ID copy karo. Ya Google Place ID Finder tool use karo."
            required
          />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Reviews <span className="text-gray-400 font-normal">(1–10)</span>
              </label>
              <select
                value={maxReviews}
                onChange={(e) => setMaxReviews(e.target.value)}
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm bg-white"
              >
                {[1,2,3,4,5,6,7,8,9,10].map((n) => (
                  <option key={n} value={n}>{n} reviews</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-400">Homepage pe kitne reviews dikhane hain</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Minimum Rating
              </label>
              <select
                value={minRating}
                onChange={(e) => setMinRating(e.target.value)}
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm bg-white"
              >
                <option value={1}>1+ stars (all)</option>
                <option value={2}>2+ stars</option>
                <option value={3}>3+ stars</option>
                <option value={4}>4+ stars (recommended)</option>
                <option value={5}>5 stars only</option>
              </select>
              <p className="mt-1 text-xs text-gray-400">Sirf is rating ya usse upar ke reviews dikhao</p>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="mt-2 w-full py-2.5 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? (
              <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> Saving...</>
            ) : (
              'Save Settings'
            )}
          </button>
        </Section>

        {/* Preview */}
        <Section title="Preview Reviews">
          <p className="text-sm text-gray-500">
            Settings save karne ke baad yahan test kar sako ge ke Google se reviews fetch ho rahe hain ya nahi.
          </p>
          <button
            onClick={handlePreview}
            disabled={previewLoading || !currentPlugin?.settings?.api_key}
            className="w-full py-2.5 px-4 rounded-xl bg-gray-800 hover:bg-gray-900 text-white text-sm font-semibold transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {previewLoading ? (
              <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> Fetching...</>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Preview Google Reviews
              </>
            )}
          </button>
          {previewData && (
            <div className="mt-4 space-y-3">
              {previewData.error ? (
                <div className="p-3.5 rounded-xl bg-red-50 border border-red-100 text-sm text-red-700">
                  Error: {previewData.error}
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 p-3.5 rounded-xl bg-gray-50 border border-gray-100">
                    <div>
                      <p className="font-semibold text-gray-800">{previewData.place_name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <StarRating rating={Math.round(previewData.overall_rating)} />
                        <span className="text-sm text-gray-500">{previewData.overall_rating} · {previewData.total_ratings?.toLocaleString()} reviews</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400">{previewData.reviews?.length} reviews mili hain (filter ke baad):</p>
                  {(previewData.reviews || []).map((r, i) => (
                    <div key={i} className="p-3.5 rounded-xl bg-gray-50 border border-gray-100">
                      <div className="flex items-center gap-2 mb-1">
                        {r.profile_photo_url ? (
                          <img src={r.profile_photo_url} alt={r.author_name} className="w-7 h-7 rounded-full object-cover" />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold">
                            {r.author_name?.[0]}
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-medium text-gray-800">{r.author_name}</p>
                          <p className="text-xs text-gray-400">{r.relative_time_description}</p>
                        </div>
                        <StarRating rating={r.rating} />
                      </div>
                      <p className="text-xs text-gray-600 leading-relaxed line-clamp-3">{r.text}</p>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </Section>

        {/* Setup Guide */}
        <Section title="Setup Instructions">
          <ol className="space-y-3 text-sm text-gray-600 list-none">
            {[
              { step: '1', text: 'Google Cloud Console (console.cloud.google.com) par jao → New project banao ya existing select karo.' },
              { step: '2', text: 'APIs & Services → Library → "Places API" search karo → Enable karo.' },
              { step: '3', text: 'APIs & Services → Credentials → Create Credentials → API Key → Copy karo.' },
              { step: '4', text: 'Apna business Google Maps par search karo → Share/Embed map → link mein "place/" ke baad wala ID copy karo. Ya Google Place ID Finder tool use karo.' },
              { step: '5', text: 'API Key aur Place ID upar paste karo → Save Settings karo → Plugin activate karo.' },
              { step: '6', text: 'Preview button se check karo ke reviews aa rahi hain ya nahi.' },
            ].map(({ step, text }) => (
              <li key={step} className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center mt-0.5">
                  {step}
                </span>
                <span>{text}</span>
              </li>
            ))}
          </ol>
          <div className="mt-3 p-3.5 rounded-xl bg-amber-50 border border-amber-100 text-xs text-amber-700 leading-relaxed">
            <strong>Note:</strong> Google Places API sirf 5 reviews return karta hai per request. Ye Google ka limitation hai — alag alag sort orders try karne ke liye multiple calls ki zaroorat hoti hai. Plugin "newest" sort use karta hai.
          </div>
        </Section>
      </div>
    </div>
  );
}
